import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, KpiCard, KPI_ROW } from '../components/ui.jsx'
import { loadActorNames, resolveActor } from '../lib/actorNames.js'
import DataTable, { StatusPill } from '../components/DataTable.jsx'
import Select from '../components/Select.jsx'
import { TagIcon, TextIcon, UserIcon, ClockIcon } from '../components/icons.jsx'
import { EVENT_TYPES, listEventsRange, localEvents, absoluteTime } from '../lib/events.js'
import { isCognitoConfigured } from '../lib/cognito.js'
import { RANGE_KEYS, RANGE_LABELS, rangeFor, isDayKey, chicagoDayOf } from '../lib/activityMetrics.js'
import {
  EVENTS_DEFAULT_RANGE,
  eventsRangeKey,
  eventsInWindow,
  oldestDay,
  eventStats,
  emptyEventsMessage,
  sourceNotice,
} from '../lib/eventsLog.js'

// Pill tone per event group.
const GROUP_TONE = {
  account: 'info',
  leads: 'warning',
  outreach: 'neutral',
  team: 'purple',
  integrations: 'success',
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'leads', label: 'Leads' },
  { key: 'outreach', label: 'Outreach' },
  { key: 'team', label: 'Team' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'account', label: 'Account' },
]

const groupOf = (type) => EVENT_TYPES[type]?.group

// The range picker wears the Activity page's classes, so the two pickers read
// as one control across the app.
const WARN_BOX =
  'mb-5 rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]'
const GHOST_BTN =
  'inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-45'
const DATE_INPUT =
  'w-[150px] max-w-full rounded-[9px] border border-line bg-surface px-[12px] py-[8px] text-[13px] text-ink focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none'

// What the table holds while a window is on the wire: one array, so the table
// is not handed a fresh empty array (and a page reset) on every render.
const NO_EVENTS = []

const eventColumns = (names) => [
  {
    key: 'event',
    header: 'Event',
    icon: TagIcon,
    render: (e) => {
      const meta = EVENT_TYPES[e.type] || { label: e.type, group: '' }
      return (
        <StatusPill tone={GROUP_TONE[meta.group] || 'neutral'}>
          {meta.label}
        </StatusPill>
      )
    },
  },
  {
    key: 'detail',
    header: 'Detail',
    icon: TextIcon,
    render: (e) => e.detail || <span className="text-faint">N/A</span>,
  },
  {
    key: 'member',
    header: 'Member',
    icon: UserIcon,
    // The roster is the authority on who a person is. Stored actorName is a
    // fallback only — old events carry email-prefix labels ("Ytsadok") and
    // demo-era role words ("Rep") that must not represent real teammates.
    render: (e) => {
      if (!e.actor) return <span className="text-faint">System</span>
      const name = resolveActor(names, e.actor, e.actorName)
      return (
        <div>
          <div className="font-semibold">{name}</div>
          {name !== e.actor && <div className="mt-[2px] text-[12px] text-faint">{e.actor}</div>}
        </div>
      )
    },
  },
  {
    key: 'when',
    header: 'When',
    icon: ClockIcon,
    render: (e) => <span title={e.createdAt}>{absoluteTime(e.createdAt)}</span>,
  },
]

// h-full + min-h-0 on the root (not min-h-full): the page has to be BOUNDED by
// the viewport for the table's own body to scroll and its footer to stay
// pinned, the same as the leads table. With min-h-full the column grows past
// the viewport and flex-1 never constrains the table.
export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams()
  // ?range=<key>, plus ?from=YYYY-MM-DD&to=YYYY-MM-DD when the key is 'custom':
  // the Activity page's link shape. No ?range= at all is All time.
  const rangeKey = eventsRangeKey(searchParams.get('range'))
  const fromParam = searchParams.get('from') || ''
  const toParam = searchParams.get('to') || ''
  const [now, setNow] = useState(() => Date.now())
  const range = useMemo(() => rangeFor(rangeKey, now, { from: fromParam, to: toParam }), [rangeKey, fromParam, toParam, now])
  const todayDay = chicagoDayOf(now)
  // 'custom' is a mode as well as a window: when the link's dates are unusable
  // rangeFor falls back to today and says so in range.notice, and the date
  // fields stay on screen so the correction is not made behind the reader's back.
  const customMode = rangeKey === 'custom'

  const [read, setRead] = useState(null) // { since, until, events, mode, status, complete }
  const [filter, setFilter] = useState('all')
  const [actorNames, setActorNames] = useState(null)
  const [dateDraft, setDateDraft] = useState(null) // null = mirror ?from / ?to

  useEffect(() => {
    let cancelled = false
    loadActorNames().then((m) => {
      if (!cancelled) setActorNames(m)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const columns = useMemo(() => eventColumns(actorNames), [actorNames])

  // EVERY event in the window, not a first page. The tiles, the tab counts and
  // the search all answer for the whole window, so they are computed from all
  // of it: a first page of 100 rows under "This month" would print 100 for any
  // busy month. The ledger is small and the server walks the range itself, so
  // this is one request for months of events.
  useEffect(() => {
    let cancelled = false
    const { since, until } = range
    ;(async () => {
      let got
      try {
        got = await listEventsRange({ since, until })
      } catch {
        got = { events: [], complete: false, mode: 'local', status: 0 }
      }
      if (cancelled) return
      if (got.mode === 'api') {
        setRead({ since, until, events: got.events, mode: 'api', status: got.status, complete: got.complete })
        return
      }
      // No answer from the server, or no Cognito to ask it with: this browser's
      // own mirror, clipped to the same window. It is the whole record only in a
      // build without Cognito; anywhere else sourceNotice says it is a copy.
      setRead({
        since,
        until,
        events: eventsInWindow(localEvents(), since, until),
        mode: 'local',
        status: got.status,
        complete: !isCognitoConfigured,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [range.since, range.until])

  // MIDNIGHT ROLLOVER. `now` is otherwise fixed at mount, so a tab left open
  // overnight on Today would keep showing yesterday's events under "Today".
  // Checking the Chicago day once a minute is enough: state only changes when
  // the day key ticks over, and then the window and the read move with it.
  useEffect(() => {
    const id = setInterval(() => {
      const at = Date.now()
      if (chicagoDayOf(at) !== todayDay) setNow(at)
    }, 60000)
    return () => clearInterval(id)
  }, [todayDay])

  // Rows are only ever shown under the window they were read for: while a new
  // window is on the wire the table spins instead of showing the last one's.
  const loading = !read || read.since !== range.since || read.until !== range.until
  const events = loading ? NO_EVENTS : read.events
  const stats = useMemo(() => eventStats(events, groupOf), [events])
  const notice = loading ? null : sourceNotice(read, isCognitoConfigured)
  // A tile prints a number only for a complete read: the server's whole window,
  // or the mirror in a build that has nothing else. A short or borrowed read
  // prints '—', unavailable, never a small number that looks whole.
  const tile = (n) => (loading ? '…' : read.complete ? n.toLocaleString() : '—')

  // Changing the window adjusts the view in place, so it REPLACES the history
  // entry instead of burying the way out under one entry per click.
  function setRangeParams(patch) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === undefined || v === '') next.delete(k)
          else next.set(k, String(v))
        }
        return next
      },
      { replace: true },
    )
  }

  const rangeOptions = RANGE_KEYS.map((k) => ({ value: k, label: RANGE_LABELS[k] }))
  // What the two date fields show: the draft while it is being edited, else the
  // link's own dates, else (whenever rangeFor had to correct them) the window
  // actually used, so the fields never disagree with the table.
  const linkDatesUsable = !range.notice
  const draftFrom = dateDraft ? dateDraft.from : linkDatesUsable && isDayKey(fromParam) ? fromParam : range.sinceDay || todayDay
  const draftTo = dateDraft ? dateDraft.to : linkDatesUsable && isDayKey(toParam) ? toParam : range.untilDay
  const customDirty = draftFrom !== fromParam || draftTo !== toParam
  const customReady = isDayKey(draftFrom) && isDayKey(draftTo)

  function chooseRange(key) {
    setDateDraft(null)
    if (key !== 'custom') {
      // The default stays the bare URL, so /logs/events keeps meaning everything.
      setRangeParams({ range: key === EVENTS_DEFAULT_RANGE ? null : key, from: null, to: null })
      return
    }
    // Custom opens on the window already on screen, so picking it changes
    // nothing until Apply. All time has no first day; start from the oldest
    // event on screen instead of collapsing the table to today.
    setRangeParams({ range: 'custom', from: range.sinceDay || oldestDay(events) || todayDay, to: range.untilDay })
  }

  function applyCustomRange() {
    if (!customReady) return
    setRangeParams({ range: 'custom', from: draftFrom, to: draftTo })
    setDateDraft(null)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Events"
        subtitle="A live activity log of everything happening in your workspace."
        actions={
          <>
            <Select
              ariaLabel="Range"
              value={rangeKey}
              onChange={chooseRange}
              options={rangeOptions}
              buttonClassName="min-w-[150px]"
            />
            {/* Two native date fields, as on the Activity page: keyboard
                accessible, localised, and their 'YYYY-MM-DD' value is the
                Chicago day key rangeFor takes. `max` stops a future day being
                picked; one that arrives in the URL is pulled back and named.
                The pair and Apply are one group, so a narrower window wraps
                them onto the next line together. No dash between the fields,
                matching the Activity page's picker. */}
            {customMode && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className={DATE_INPUT}
                  type="date"
                  aria-label="Range start date"
                  title="First day of the range (America/Chicago)"
                  value={draftFrom}
                  max={isDayKey(draftTo) && draftTo < todayDay ? draftTo : todayDay}
                  onChange={(e) => setDateDraft({ from: e.target.value, to: draftTo })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyCustomRange()
                  }}
                />
                <input
                  className={DATE_INPUT}
                  type="date"
                  aria-label="Range end date"
                  title="Last day of the range, included in full (America/Chicago)"
                  value={draftTo}
                  min={isDayKey(draftFrom) ? draftFrom : undefined}
                  max={todayDay}
                  onChange={(e) => setDateDraft({ from: draftFrom, to: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyCustomRange()
                  }}
                />
                <button
                  type="button"
                  className={GHOST_BTN}
                  onClick={applyCustomRange}
                  disabled={!customReady || !customDirty}
                  title={customReady ? 'Show the events recorded on these days' : 'Pick both a start and an end date'}
                >
                  Apply
                </button>
              </div>
            )}
          </>
        }
      />

      {/* rangeFor had to alter what the URL asked for (a reversed pair, a date
          in the future, a half-written link). The table is for the window
          named here, and saying so is the point. */}
      {range.notice && <div className={WARN_BOX}>{range.notice}</div>}
      {notice &&
        (notice.tone === 'warn' ? (
          <div className={WARN_BOX}>{notice.text}</div>
        ) : (
          <p className="m-0 mb-4 text-[13px] text-dim">{notice.text}</p>
        ))}

      <section className={`mb-6 ${KPI_ROW}`}>
        <KpiCard label="Events" value={tile(stats.total)} />
        <KpiCard label="Emails sent" value={tile(stats.emailsSent)} />
        <KpiCard label="Active members" value={tile(stats.activeMembers)} />
      </section>

      {/* The table is `fill`, so its root is h-full: it needs its own flex-1
          box or it claims the whole column. It always renders, even for an
          empty window, so the tabs and search stay put while a range loads. */}
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          rows={events}
          rowKey={(e) => e.id}
          searchable
          searchPlaceholder="Search events…"
          searchValue={(e) =>
            `${EVENT_TYPES[e.type]?.label || e.type} ${e.detail || ''} ${e.actorName || ''} ${e.actor || ''}`
          }
          tabs={FILTERS.map((f) => ({
            key: f.key,
            label: f.label,
            count: loading ? null : f.key === 'all' ? stats.total : stats.byGroup[f.key] || 0,
            predicate: f.key === 'all' ? undefined : (e) => groupOf(e.type) === f.key,
          }))}
          activeTab={filter}
          onTabChange={setFilter}
          emptyMessage={events.length ? 'No events match this filter.' : emptyEventsMessage(range)}
          loading={loading}
          loadingMessage="Loading events…"
          // Every row in the window is in memory; pages of 100 keep a long
          // window (All time) from rendering thousands of rows at once.
          pageSize={100}
          fill
          // The audit log is read by scanning a long column of rows, not by
          // studying one — tighter rows put more of it on screen at once.
          dense
        />
      </div>
    </div>
  )
}
